import {Box} from "@mui/material";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {useOrganizationGetQuery, useRoadsoftConfigQuery} from "../../../app/api/api";
import QueryErrorAlert from "../../../components/QueryErrorAlert";
import OrganizationGeneralContent from "./OrganizationGeneralContent";
import {useTranslation} from "react-i18next";
import {formatTimestamp} from "../../../utils/datetime";
import React, {useMemo} from "react";
import DKVCustomerNumberTable from "../DKVCustomerNumberTable";
import StageTable from "../StageTable";
import {ContentTitle} from "../../../components/Layout/Page";
import AdminUserTable from "../AdminUserTable";
import EetsDeviceList from "./EetsDeviceList";
import {ErrorCode} from "../../../components/Layout/Page/types";
import ErrorPage from "../../../components/Layout/Page/ErrorPage";
import {skipToken} from "@reduxjs/toolkit/dist/query";
import {OrganizationField} from "../../../app/api/model/organization";
import DeviceJobList from "./DeviceJobList";
import TabSection from "../../../components/Layout/Page/TabSection";
import RoadsoftDetails from "./RoadsoftDetails";

export default function OrganizationDetails() {
    const {t} = useTranslation();
    const params = useParams();
    const location = useLocation();
    const organizationId = Number.parseInt(params.organizationId || "0");
    const {data: organization, error} = useOrganizationGetQuery(organizationId || skipToken);
    const {data: roadsoftConfig} = useRoadsoftConfigQuery(organizationId);
    const navigate = useNavigate();

    const initialTab = useMemo(() => {
        const fromHash = Number.parseInt((location.hash || "").replace("#", ""), 10);
        return Number.isFinite(fromHash) && fromHash >= 0 ? fromHash : 0;
    }, [location.hash]);

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        navigate(`#${newValue}`, {replace: true});
    };

    return (
        <Box>
            {organization ? (
                <>
                    <ContentTitle
                        titleKey="generic_organization"
                        subtitle={organization?.name}
                        entry={organization}
                        toolEntries={[
                            {
                                translationKey: "organizations_lockedAt",
                                fieldName: OrganizationField.LOCKED_AT,
                                render: (lockedAt) => formatTimestamp(lockedAt),
                            },
                        ]}
                    />
                    <TabSection
                        title={t("generic_organizations")}
                        initialTab={initialTab}
                        onChange={handleChange}
                        tabs={[
                            {
                                label: t("generic_general"),
                                content: <OrganizationGeneralContent organization={organization} />,
                            },
                            {
                                label: t("organizations_dkvCustomerNumbers"),
                                content: <DKVCustomerNumberTable organizationId={organizationId} />,
                            },
                            {
                                label: t("organizations_adminUsers"),
                                content: <AdminUserTable organizationId={organizationId} />,
                            },
                            {
                                label: t("organizations_eetsDevices"),
                                content: <EetsDeviceList organizationId={organizationId} />,
                            },
                            {
                                label: t("generic_stages"),
                                content: (
                                    <StageTable organizationId={organizationId} allStages={organization.allStages} />
                                ),
                            },
                            {
                                label: t("organizations_deviceJobList"),
                                content: <DeviceJobList organizationId={organizationId} />,
                            },
                            {
                                label: t("generic_roadsoft"),
                                content: (
                                    <RoadsoftDetails
                                        roadsoftConfig={roadsoftConfig}
                                        organizationId={organizationId}
                                        organizationCountry={organization.country}
                                    />
                                ),
                            },
                        ]}
                    />
                </>
            ) : (
                <ErrorPage code={ErrorCode.NOT_FOUND}>{error && <QueryErrorAlert error={error} />}</ErrorPage>
            )}
        </Box>
    );
}
